// src/components/songEdit/search/TuneSearch.tsx
import React, { useState, useRef } from 'react';
import { Plus, Edit } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import { useClickOutside } from '../hooks/useClickOutside';
import { TuneForm } from '../forms/TuneForm';
import { Tune } from '../types/tuneTypes';
import apiClient from '../../../utils/apiClient';

interface TuneSearchProps {
  value: string;
  onChange: (tune: Tune | null) => void;
  placeholder?: string;
}

export const TuneSearch: React.FC<TuneSearchProps> = ({ value, onChange, placeholder = "Search tunes..." }) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [results, setResults] = useState<Tune[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showTuneForm, setShowTuneForm] = useState(false);
  const [editingTune, setEditingTune] = useState<Tune | null>(null);
  const [selectedTune, setSelectedTune] = useState<Tune | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useClickOutside(searchRef, () => {
    setShowDropdown(false);
  });

  // Update searchTerm when value prop changes
  React.useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  React.useEffect(() => {
    if (!isSearching) return;

    const searchTunes = async () => {
      if (debouncedSearchTerm.trim().length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await apiClient.get('/tunes/search', {
          params: { term: debouncedSearchTerm }
        });
        setResults(response.data.tunes);
        setShowDropdown(true);
      } catch (error) {
        console.error('Error searching tunes:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    searchTunes();
  }, [debouncedSearchTerm, isSearching]);

  const handleSelect = (tune: Tune) => {
    // For alternate tunes (value=""), don't fill the search term
    if (value) {
      setSearchTerm(tune.tune_name);
    } else {
      setSearchTerm(''); // Clear search for alternate tunes
    }
    setSelectedTune(tune);
    onChange(tune);
    setShowDropdown(false);
    setIsSearching(false);
    inputRef.current?.blur();
  };

  const handleAddNew = () => {
    setEditingTune({ tune_name: searchTerm, meter_id: undefined, is_preferred: false });
    setShowTuneForm(true);
    setShowDropdown(false);
  };

  const handleEdit = () => {
    if (selectedTune) {
      setEditingTune(selectedTune);
      setShowTuneForm(true);
    }
  };

  const handleTuneSubmit = (tune: Tune) => {
    console.log('[TuneSearch] Submitting tune:', tune);
    // For new tunes (no tune_id), don't assign a temporary ID
    // The backend will handle creation when the song is saved
    const tuneForSelection: Tune = {
      tune_name: tune.tune_name,
      meter_id: tune.meter_id,
      is_preferred: tune.is_preferred || false,
      // Only include tune_id if it already exists (editing existing tune)
      ...(tune.tune_id && { tune_id: tune.tune_id })
    };
    
    handleSelect(tuneForSelection);
    setShowTuneForm(false);
    setEditingTune(null);
  };

  const handleTuneFormClose = () => {
    setShowTuneForm(false);
    setEditingTune(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setIsSearching(true);
    setSearchTerm(newValue);

    // If the field is cleared, notify parent to remove the tune
    if (newValue === '' && (selectedTune || value)) {
      setSelectedTune(null);
      onChange(null);
    }
  };

  const handleFocus = () => {
    if (isSearching) {
      setShowDropdown(true);
    }
  };

  return (
    <div className="tune-search position-relative" ref={searchRef}>
      <div className="input-group">
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
        />
        <button 
          type="button"
          className="btn btn-secondary"
          onClick={handleAddNew}
          title="Add new tune"
        >
          <Plus size={20} />
        </button>
        {selectedTune && (
          <button 
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleEdit}
            title="Edit selected tune"
          >
            <Edit size={20} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="tune-search__dropdown position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{zIndex: 1000}}>
          {isLoading ? (
            <div className="p-3 text-center">Loading...</div>
          ) : results.length > 0 ? (
            <ul className="list-unstyled mb-0">
              {results.map((tune) => (
                <li
                  key={tune.tune_id}
                  className="p-2"
                  style={{cursor: 'pointer'}}
                  onClick={() => handleSelect(tune)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div>{tune.tune_name}</div>
                  {tune.is_preferred && (
                    <small className="text-success">Preferred</small>
                  )}
                </li>
              ))}
            </ul>
          ) : searchTerm.length >= 2 ? (
            <div>
              <div className="p-3 text-center">No tunes found</div>
              <div className="border-top">
                <button 
                  className="btn btn-link w-100 text-start p-3"
                  onClick={handleAddNew}
                >
                  <Plus size={16} className="me-2" />
                  Add new tune: "{searchTerm}"
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {showTuneForm && editingTune && (
        <TuneForm
          initialTune={editingTune}
          onSubmit={handleTuneSubmit}
          onClose={handleTuneFormClose}
        />
      )}
    </div>
  );
};